import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { lettersNumbersAndSpecialsRegex } from 'app/common/utils';
import { Success } from 'app/components/Notifications';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import useClickOutside from 'app/hooks/useClickOutside';
import clsx from 'clsx';
import { useNotificationsContext } from 'context';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { userInfoApi } from 'services/api/user-info/queries';
import z from 'zod/v3';

interface Props {
  initialValue: string;
  onChange: (value: string) => void;
  close: () => void;
}

const schema = z.object({
  name: z
    .string()
    .trim()
    .max(100, { message: 'maximum_length_exceeded' })
    .regex(lettersNumbersAndSpecialsRegex)
    .or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

export default function EditCompanyNameDialog({
  initialValue,
  onChange,
  close,
}: Props) {
  const { t } = useTranslation();

  const [updateUser, { isLoading }] = userInfoApi.useUpdateUserInfoMutation();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { addNotification } = useNotificationsContext();

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(close);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      name: initialValue,
    },
  });

  async function onSubmit({ name }: FormData) {
    try {
      await updateUser({
        companyName: name === '' ? null : name,
      });
      addNotification(<Success text={t('success_company_name_update')} />);
      onChange(name);
      close();
    } catch (error) {
      addErrorModal();
    }
  }

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            close();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-value="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          <h2>{t('edit_company_name')}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={clsx('form-group', { error: !!errors.name })}>
              <div className="form-content">
                <div className="form-label">
                  <span>{t('company_name')}</span>
                </div>
                <div className="form-control">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        autoComplete="off"
                        placeholder={t('enter_company_name')}
                        disabled={isLoading}
                        onChange={e => {
                          const nextValue = e.value || '';

                          const cleanValue = nextValue
                            .split('')
                            .filter(char =>
                              lettersNumbersAndSpecialsRegex.test(char),
                            )
                            .join('');

                          field.onChange(cleanValue);
                        }}
                      />
                    )}
                  />
                </div>
              </div>

              {errors.name?.message && (
                <div className="form-error">
                  <Error>
                    {t<string>(errors.name.message, { length: 100 })}
                  </Error>
                </div>
              )}
            </div>

            <Button themeColor="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <div className="loading_icon loading_icon-btn">
                  <div className="css-icon"></div>
                </div>
              ) : (
                t('save')
              )}
            </Button>
          </form>
        </>
      </div>
    </Dialog>
  );
}
